import { useEffect, useState, useMemo } from "react";
import "./liveTrackTable.css";
import { convertToLocalTime } from "@utils/dateAndTimeUtils";
import { exportLiveTrackToExcel } from "@components/liveTrack/exportLiveTrack";
import { defaultTimezone } from "@components/ui/timezone/timezoneMappings";

const LiveTrackTable = ({
  agmData = [],
  isEditable = false,
  onCellClick,
  onCellChange,
  onCellBlur,
  timezone = defaultTimezone,
  jobName,
  runName,
  notificationsPaused
}) => {
  const [localEdits, setLocalEdits] = useState({}); // Track local input edits by ALTID
  // Compute flattened data synchronously using useMemo
  const editedData = useMemo(() => {
    if (!Array.isArray(agmData) || agmData.length === 0) return [];

    return agmData
      .filter(agm => agm.AGMs && agm.AGMs.length > 0) // Guard: skip rows with missing/empty AGMs
      .map((agm, index) => {
        const flattened = {
          ...agm,
          ...agm.AGMs[0],
          // Preserve calculated fields - AGMs[0] may have null values that overwrite
          Est_PassTime: agm.Est_PassTime,
          timeTo: agm.timeTo,
        };

        // Apply any local edits for this row
        if (localEdits[agm.ALTID]) {
          Object.assign(flattened, localEdits[agm.ALTID]);
        }

        return flattened;
      });
  }, [agmData, localEdits]);


  // Preserve local edits for rows that still exist in agmData, clear edits for removed rows
  useEffect(() => {
    if (Object.keys(localEdits).length > 0) {
      // console.log('[LiveTrackTable] agmData changed - preserving localEdits for existing rows');
      const currentALTIDs = new Set(agmData.map(agm => String(agm.ALTID)));
      setLocalEdits(prev => {
        const preserved = {};
        for (const altid of Object.keys(prev)) {
          if (currentALTIDs.has(String(altid))) {
            preserved[altid] = prev[altid];
          }
        }
        // console.log('[LiveTrackTable] localEdits preserved:', preserved);
        return preserved;
      });
    }
  }, [agmData]); //eslint-disable-line

  //handle the site_notes and Box Number inputs
  const handleInputChange = (index, field, value) => {
    const agm = editedData[index];
    if (!agm) return;

    // console.log('[LiveTrackTable] handleInputChange:', { index, field, value, altId: agm.ALTID });

    // Find original agmData item by ALTID for onCellChange
    const originalAgm = agmData.find(a => a.ALTID === agm.ALTID);

    // Update local edits
    setLocalEdits(prev => {
      const newEdits = {
        ...prev,
        [agm.ALTID]: {
          ...(prev[agm.ALTID] || {}),
          [field]: value
        }
      };
      // console.log('[LiveTrackTable] localEdits updated:', { altId: agm.ALTID, field, value, newEdits });
      return newEdits;
    });

    if (onCellChange && originalAgm && originalAgm.AGMs?.[0]) {
      const updatedAgm = {
        ...originalAgm,
        AGMs: [{ ...originalAgm.AGMs[0], [field]: value }],
      };
      // console.log('[LiveTrackTable] Calling onCellChange with:', updatedAgm);
      onCellChange(updatedAgm);
    }
  };

  //user has clicked out of input
  //save the data
  const handleBlur = (event, agm, index, field) => {
    if (onCellBlur) {
      // console.log('[LiveTrackTable] Calling onCellBlur with agm:', agm);
      onCellBlur(agm);
    }
  };

  //check if estimated pass time in the past
  //use for changing text colour in table
  const checkEstPassTimeinPast = (estPassTime) => {
    if (!estPassTime) return false;
    const estPassTimestamp = new Date(estPassTime).getTime();
    const currentDateTimestamp = new Date().getTime();

    if (estPassTimestamp < currentDateTimestamp) {
      return true;
    }

    return false;
  };

  const handleExportLiveTrackExcel = async() => {
    const jobData = {
      jobName,
      runName,
      tz: timezone
    }

    await exportLiveTrackToExcel(editedData, jobData);
  }


  if (!Array.isArray(agmData) || agmData.length === 0) return null;

  return (
    <>

    {/* above-table info */}

    <div className="flex justify-between">

      {/* notifications paused message */}
      <p className="text-red-500 text-md font-bold mb-1 md:mb-0 self-end">
          {notificationsPaused ? <><i className="fa-solid fa-circle-exclamation"></i> Notifications are paused. Estimated pass times may be inaccurate.</> : null}
      </p>

      {/* export button, timezone info */}
      <div className="flex flex-col">

        <button
          className="border p-1 mb-4 bg-green-100 rounded-lg hover:scale-105 hover:font-semibold max-w-max self-end"
          onClick={handleExportLiveTrackExcel}
        >Export as Excel</button>
        
        <h1 className="italic text-red-500">*Time and Date Displayed in <span className="font-bold underline">{timezone.label}</span></h1>

      </div>
    </div>

    {/* table */}

    <div className="table-container">
      <table className="min-w-[1500px] w-full divide-y divide-gray-200">
        <thead className="sticky-header">
          <tr>
            <th className="sticky-left sticky-left-header">Site Name</th>
            <th>Pass Time</th>
            <th>Estimated Pass Time</th>
            <th>Real Chain (km)</th>
            <th>Speed (km/h)</th>
            <th>Time To (mins)</th>
            <th>Passage Notes</th>
            <th>Box Number</th>
            <th>Site Notes</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {editedData.map((agm, index) => (
            <tr
              className={`${
                agm.PassTime && !agm.Approved && isEditable ? "bg-red-400" : ""
              } text-center border-cells`}
              key={agm.ALTID}
            >
              <td className="sticky-left max-w-24 sm:max-w-60 px-2 py-1">{agm.Site_Name || "N/A"}</td>
              <td
                onClick={isEditable ? () => onCellClick(agm) : null}
                className={`${
                  isEditable ? "cursor-pointer" : ""
                } text-blue-900`}
              >
                {!isEditable && agm.PassTime && agm.Approved === true
                  ? convertToLocalTime(agm.PassTime, timezone.value)
                  : isEditable && agm.PassTime
                  ? convertToLocalTime(agm.PassTime, timezone.value)
                  : isEditable
                  ? "Click To Set"
                  : "-"}
              </td>
              <td
                className={
                  checkEstPassTimeinPast(agm.Est_PassTime) ? "text-red-400" : ""
                }
              >
                {agm.Est_PassTime && !agm.PassTime
                  ? convertToLocalTime(agm.Est_PassTime, timezone.value)
                  : agm.Est_PassTime &&
                    agm.PassTime &&
                    !agm.Approved &&
                    !isEditable
                  ? convertToLocalTime(agm.Est_PassTime, timezone.value)
                  : ""}
              </td>
              <td>{agm.Real_Chain != null ? agm.Real_Chain.toFixed(5) : 'N/A'}</td>
              <td>{agm.Speed}</td>
              <td>{agm.timeTo ? agm.timeTo : ""}</td>
              <td>
                {isEditable ? (
                  <input
                    type="text"
                    value={editedData[index]?.Pass_Notes || ""}
                    onChange={(e) =>
                      handleInputChange(index, "Pass_Notes", e.target.value)
                    }
                    className="border p-1"
                    onBlur={(e) => handleBlur(e, agm, index, "Pass_Notes")}
                  />
                ) : (
                  agm.Pass_Notes || ""
                )}
              </td>
              <td>
                {isEditable ? (
                  <input
                    type="text"
                    value={editedData[index]?.Box_Number || ""}
                    onChange={(e) =>
                      handleInputChange(index, "Box_Number", e.target.value)
                    }
                    className="border p-1"
                    onBlur={(e) => handleBlur(e, agm, index, "Box_Number")}
                  />
                ) : (
                  agm.Box_Number || ""
                )}
              </td>
              <td>
                {isEditable ? (
                  <input
                    type="text"
                    value={editedData[index]?.Site_Notes || ""}
                    onChange={(e) =>
                      handleInputChange(index, "Site_Notes", e.target.value)
                    }
                    className="border p-1"
                    onBlur={(e) => handleBlur(e, agm, index, "Site_Notes")}
                  />
                ) : (
                  agm.Site_Notes || ""
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default LiveTrackTable;
